/**
 * @description Renders invoice payment details, gateway availability, and payment submission.
 *
 * @dependencies
 * - React Router: Reads portal context and navigates within the active public portal token.
 * - portalService: Creates payment sessions and submits vaulted payment details.
 * - PortalPaymentForm: Displays gateway-aware payment controls.
 *
 * @notes
 * - Invoice-detail navigation remains available when the merchant has no payment gateway configured.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import portalService from '../../services/portalService';
import PortalPaymentForm from './PortalPaymentForm';
import SurchargeConsentModal from '../shared/SurchargeConsentModal';
import PaymentErrorBanner from '../shared/PaymentErrorBanner';
import { formatCurrency } from '../../utils/amountUtils';

function PortalPayment({ portalData: propsPortalData }) {

const location = useLocation();
const navigate = useNavigate();
const context = useOutletContext() || {};
const { token } = context;

// Use portalData from context if not provided as prop
const portalData = propsPortalData || context.portalData;

// Merge all possible data sources
const mergedData = {
  ...context,
  ...location.state,
  ...(portalData && { portalData })
};

// Get publicId from multiple sources
const publicId = portalData?.customerPublicId ||
                 mergedData?.publicId ||
                 mergedData?.customerPublicId || 
                 mergedData?.customerInfo?.customer?.publicId || 
                 mergedData?.portalData?.customerPublicId;

  // Get invoice IDs - memoized to prevent recreation
  const invoiceIdsFromProps = useMemo(() => {
    // Only use invoice ID from token if we're on the pay route AND there's no navigation state
    if (location.pathname.includes('/pay') && portalData?.tokenData?.invoiceId && !location.state?.selectedInvoices) {
      return [portalData.tokenData.invoiceId];
    }
    return null;
  }, [portalData?.tokenData?.invoiceId, location.pathname, location.state?.selectedInvoices]);
  
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSession, setPaymentSession] = useState(null);
  const [error, setError] = useState(null);
  const [sessionCreated, setSessionCreated] = useState(false);
  const [applyCredit, setApplyCredit] = useState(true); // Default to applying credit

  // Surcharge two-phase state
  const [surchargeInfo, setSurchargeInfo] = useState(null); // { adviceId, surchargeRate, surchargeAmountCents, adjustedTotalCents }
  const [surchargeConsentPending, setSurchargeConsentPending] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null); // Held between Phase 1 and Phase 2

  // Idempotency key — persists across retries within the same payment session
  const idempotencyKeyRef = useRef(null);

  // Determine if this is a single invoice payment from token
  const isDirectPayment = portalData?.tokenData?.type === 'invoice';
  const isSingleInvoice = selectedInvoices.length === 1;

  const createPaymentSession = useCallback(async (invoiceIds) => {
    if (!invoiceIds || invoiceIds.length === 0) return;
    
    try {
      setLoading(true);
      const session = await portalService.createPaymentSession(publicId, invoiceIds);
      setPaymentSession(session);
      setInvoiceDetails(session.invoices);
      setSessionCreated(true);
    } catch (error) {
      // Check if the error is because invoice is already paid
      if (error.response?.data?.error?.includes('already paid') ||
          error.response?.data?.error?.includes('Invalid invoice')) {
        navigate('../payment-result', {
          state: {
            paymentResult: {
              success: true,
              message: 'This invoice has already been paid.',
              transactionId: 'N/A',
              paymentDate: new Date().toISOString(),
              totalAmount: 0
            }
          }
        });
        return;
      }

      setError(error.response?.data?.error || 'Failed to load payment details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [publicId, navigate]);

  useEffect(() => {
    // Skip if session already created
    if (sessionCreated) return;
    
    // Check if we have publicId
    if (!publicId) {
      return;
    }

    // Get selected invoice IDs from props or navigation state
    const invoiceIds = invoiceIdsFromProps || location.state?.selectedInvoices || [];
    
    // Only check if the TOKEN's invoice is paid when we're actually trying to pay that specific invoice
    // If user navigated to pay a different invoice, don't check the token's original invoice status
    const isPayingTokenInvoice = portalData?.tokenData?.invoiceId && 
                                  invoiceIds.length === 1 && 
                                  invoiceIds[0] === portalData.tokenData.invoiceId;
    
    if (isPayingTokenInvoice && portalData?.tokenData?.invoiceStatus === 'paid') {
      // Token invoice is already paid — redirect to result
      navigate('../payment-result', {
        state: {
          paymentResult: {
            success: true,
            message: 'This invoice has already been paid.',
            transactionId: 'N/A',
            paymentDate: new Date().toISOString(),
            totalAmount: 0
          }
        }
      });
      return;
    }
    
    if (invoiceIds.length === 0) {
      navigate('../invoices');
      return;
    }
    
    setSelectedInvoices(invoiceIds);
    createPaymentSession(invoiceIds);
  }, [publicId, invoiceIdsFromProps, location.state, navigate, createPaymentSession, sessionCreated, portalData, token]);

  // formatCurrency utility now imported from amountUtils

  /**
   * Two-phase payment flow with server-side surcharge:
   *
   * Phase 1 (handleSubmitPayment): Card submitted via HPF → vault + surcharge check
   *   - If surcharge applies: pause, show consent modal, wait for user
   *   - If no surcharge (debit card): proceed directly to Phase 2
   *
   * Phase 2 (executePayment): User consented (or no surcharge) → process payment
   *   - Sends surchargeAdviceId + shopperId to backend
   *   - Elavon charges base + surcharge natively
   */
  const handleSubmitPayment = async (paymentDetails) => {
    if (processing) return;

    try {
      setProcessing(true);
      setError(null);

      // Credit-only payments skip the surcharge flow entirely
      const isCreditOnlyPayment = paymentDetails.creditOnly === true;

      if (isCreditOnlyPayment) {
        await executePayment(paymentDetails, null);
        return;
      }

      // PCI DSS: ensure no raw card data
      if (paymentDetails.cardNumber || paymentDetails.cvv) {
        throw new Error('PCI DSS Violation: Raw card data detected in payment submission');
      }

      // Phase 1: Vault the card and check surcharge eligibility
      // Amount is computed server-side from invoiceIds (fraud prevention — never trust client amounts)
      const vaultResult = await portalService.vaultAndCheckSurcharge(publicId, {
        hostedCardId: paymentDetails.token,
        invoiceIds: selectedInvoices,
        customerData: { fullName: paymentDetails.cardName }
      });

      // Store card info for Phase 2
      const enrichedPaymentDetails = {
        ...paymentDetails,
        // Replace HPF hostedCardId with vaulted storedCard info for backend
        storedCardId: vaultResult.storedCard.id,
        gatewayCardId: vaultResult.storedCard.gatewayCardId,
        shopperId: vaultResult.storedCard.gatewayShopperId,
        last4: vaultResult.storedCard.lastFour || paymentDetails.last4,
        cardBrand: vaultResult.storedCard.cardBrand || paymentDetails.cardBrand
      };

      if (vaultResult.surcharge) {
        // Surcharge applies — pause and show consent modal
        setSurchargeInfo(vaultResult.surcharge);
        setPendingPaymentData(enrichedPaymentDetails);
        setSurchargeConsentPending(true);
        setProcessing(false); // Pause spinner while modal is shown
      } else {
        // No surcharge (debit card or merchant not enrolled) — charge immediately
        await executePayment(enrichedPaymentDetails, null);
      }

    } catch (error) {
      setError(error);
      setProcessing(false);
    }
  };

  /** Accept surcharge — resume payment with surchargeAdviceId */
  const handleSurchargeAccept = async () => {
    setSurchargeConsentPending(false);
    setProcessing(true);
    try {
      await executePayment(pendingPaymentData, surchargeInfo.adviceId);
    } catch (error) {
      setError(error);
      setProcessing(false);
    }
  };

  /** Decline surcharge — refresh HPF with a new session so customer can retry */
  const handleSurchargeDecline = () => {
    setSurchargeConsentPending(false);
    setSurchargeInfo(null);
    setPendingPaymentData(null);
    // The current HPF session is consumed (hostedCardCreated already fired).
    // Reset sessionCreated to trigger useEffect → createPaymentSession() → fresh HPF fields.
    setSessionCreated(false);
  };

  /** Phase 2: Execute the actual payment */
  const executePayment = async (paymentDetails, surchargeAdviceId) => {
    const isCreditOnlyPayment = paymentDetails.creditOnly === true;

    const creditToApply = applyCredit && parseFloat(paymentSession?.availableCredit) > 0
      ? Math.min(
          parseFloat(paymentSession?.availableCredit),
          parseFloat(paymentSession?.totalAmount)
        )
      : 0;

    const paymentData = {
      sessionToken: paymentSession.sessionToken,
      invoiceIds: selectedInvoices,
      paymentMethod: isCreditOnlyPayment ? 'credit' : (paymentDetails.method || 'card'),
      applyCredit: applyCredit && creditToApply > 0,
      creditAmountToApply: creditToApply,
      paymentDetails: isCreditOnlyPayment ? {
        method: 'credit',
        creditOnly: true
      } : {
        // Phase 2: charge the StoredCard that Phase 1 (/vault-and-check-surcharge) already vaulted.
        // Do NOT send the HostedCard token here — Elavon would 400 ("Stored card already exists"),
        // our delete+recreate path would fire, and the resulting StoredCard would no longer match
        // the one referenced by surchargeAdvice. The merchant flow does the same thing — see
        // frontend/src/components/invoices/PaymentRecorder.js:243-258.
        storedCardId: paymentDetails.storedCardId,
        method: paymentDetails.method,
        last4: paymentDetails.last4,
        brand: paymentDetails.cardBrand,
        expiryMonth: paymentDetails.expiryMonth,
        expiryYear: paymentDetails.expiryYear,
        cardholderName: paymentDetails.cardName,
        billingAddress: paymentDetails.billingAddress,
        billingCity: paymentDetails.billingCity,
        billingState: paymentDetails.billingState,
        billingZip: paymentDetails.billingZip,
        // Server-side surcharge: pass advice ID + shopper ID for Elavon-native surcharge
        surchargeAdviceId: surchargeAdviceId || undefined,
        shopperId: paymentDetails.shopperId || undefined
      }
    };

    // Generate idempotency key once per payment attempt (persists across retries)
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }

    const result = await portalService.processPayment(publicId, paymentData, { idempotencyKey: idempotencyKeyRef.current });

    // Payment succeeded — clear idempotency key
    idempotencyKeyRef.current = null;

    setTimeout(() => {
      navigate('../payment-result', {
        state: {
          paymentResult: result,
          invoices: invoiceDetails
        },
        replace: true
      });
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error && !paymentSession) {
    return (
      <div className="py-12 max-w-md mx-auto">
        <PaymentErrorBanner error={error} onDismiss={() => setError(null)} className="mb-4" />
        <div className="text-center">
          <button
            onClick={() => navigate('../invoices')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {isDirectPayment ? 'Make a Payment' : 'Checkout'}
        </h2>
        <p className="text-gray-600 mt-1">
          {isDirectPayment && invoiceDetails.length === 1 
            ? `Pay invoice ${invoiceDetails[0].invoiceNumber}` 
            : 'Complete your payment'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment Form - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Selected Invoices */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {isDirectPayment && isSingleInvoice ? 'Invoice Details' : 'Selected Invoices'}
              </h3>
              {isSingleInvoice && invoiceDetails[0]?.invoiceNumber && (
                <button
                  type="button"
                  onClick={() => navigate(`../invoices/${invoiceDetails[0].invoiceNumber}`)}
                  className="self-start sm:self-auto px-4 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
                >
                  View Invoice
                </button>
              )}
            </div>
            <div className="space-y-3">
              {invoiceDetails.map((invoice) => (
                <div key={invoice.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                    <p className="text-sm text-gray-600">Due: {formatCurrency(invoice.amountDue)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(invoice.amountDue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method - Show only if amount due after credit > 0 */}
          {(() => {
            const effectiveAmount = applyCredit && parseFloat(paymentSession?.availableCredit) > 0
              ? parseFloat(paymentSession?.amountDueAfterCredit)
              : parseFloat(paymentSession?.totalAmount);

            // If credit fully covers the invoice
            if (effectiveAmount <= 0) {
              return (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Apply Credit</h3>
                  <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Your available credit of <span className="font-semibold text-green-600">{formatCurrency(paymentSession?.availableCredit)}</span> fully covers this invoice.
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                      No card payment is required. Click below to apply your credit.
                    </p>
                    <button
                      onClick={() => handleSubmitPayment({ method: 'credit', creditOnly: true })}
                      disabled={processing}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <>
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Applying Credit...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Apply Credit & Complete Payment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            }

            // Normal card payment (with or without partial credit)
            return (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                <PortalPaymentForm
                  onSubmit={handleSubmitPayment}
                  amount={effectiveAmount}
                  processing={processing}
                  error={error ? (error.response?.data?.error || error.message || 'Payment failed. Please try again.') : null}
                  customerPublicId={publicId}
                />
              </div>
            );
          })()}
        </div>

        {/* Order Summary - 1 column */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isDirectPayment && isSingleInvoice ? 'Payment Summary' : 'Order Summary'}
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {invoiceDetails.length === 1
                    ? 'Invoice Total'
                    : `Subtotal (${invoiceDetails.length} invoices)`}
                </span>
                <span className="font-medium">{formatCurrency(paymentSession?.totalAmount)}</span>
              </div>

              {/* Credit Option - Show if customer has available credit */}
              {parseFloat(paymentSession?.availableCredit) > 0 && (
                <div className="flex justify-between items-center text-sm py-2 border-t border-gray-100">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={applyCredit}
                      onChange={(e) => setApplyCredit(e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                    />
                    <span className={applyCredit ? 'text-green-600' : 'text-gray-600'}>
                      {applyCredit ? 'Credit Applied' : 'Apply Credit'}
                    </span>
                  </label>
                  <span className={`font-medium ${applyCredit ? 'text-green-600' : 'text-gray-500'}`}>
                    {applyCredit ? '-' : ''}{formatCurrency(
                      Math.min(
                        parseFloat(paymentSession?.availableCredit) || 0,
                        parseFloat(paymentSession?.totalAmount) || 0
                      )
                    )}
                  </span>
                </div>
              )}

              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold">
                    {applyCredit && parseFloat(paymentSession?.availableCredit) > 0 ? 'Amount to Pay' : 'Total Due'}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(
                      applyCredit && parseFloat(paymentSession?.availableCredit) > 0
                        ? (paymentSession?.amountDueAfterCredit || paymentSession?.totalAmount)
                        : paymentSession?.totalAmount
                    )}
                  </span>
                </div>
              </div>

              {/* Credit Applied Notice */}
              {applyCredit && parseFloat(paymentSession?.availableCredit) > 0 && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  {parseFloat(paymentSession?.amountDueAfterCredit) <= 0
                    ? 'Your credit fully covers this invoice. No additional payment required.'
                    : `Your available credit of ${formatCurrency(paymentSession?.availableCredit)} is being applied to this payment.`
                  }
                </div>
              )}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Payment Security</h4>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-500 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  SSL encrypted connection
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-500 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  PCI compliant processing
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-500 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  No card data stored
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <SurchargeConsentModal
        show={surchargeConsentPending && !!surchargeInfo}
        surchargeInfo={surchargeInfo}
        onAccept={handleSurchargeAccept}
        onDecline={handleSurchargeDecline}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}

export default PortalPayment;